import { Button } from "@/components/ui/button";
import { scrollToForm, trackEvent } from "@/lib/tracking";
import {
  ArrowRight, ArrowDown, MousePointerClick, UserPlus, MailCheck,
  TrendingUp, Sparkles, CheckCircle2, Globe,
} from "lucide-react";

const ACCENT = "#BEF264"; // electric lime

/* Scroll to the journey graphic if present, else the quote form */
function seeHowItWorks() {
  trackEvent("cta_click", { location: "sales_hero_secondary", target: "how_it_works" });
  const el = document.getElementById("demo") || document.getElementById("request-plan");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

const PILL_STAGES = [
  "Website", "Lead Capture", "CRM", "Automated Follow-Up", "Sales Pipeline", "Attribution",
];

/* ---------- Connected journey visual: Visitor → Enquiry → CRM → Follow-Up → Opportunity ---------- */
function JourneyVisual() {
  return (
    <div className="relative w-full max-w-[440px] mx-auto">
      {/* soft glow behind */}
      <div aria-hidden className="absolute inset-0 -z-10">
        <div className="absolute top-6 left-1/2 -translate-x-1/2 h-72 w-72 rounded-full blur-3xl" style={{ background: `${ACCENT}22` }} />
      </div>

      {/* flow label */}
      <div className="flex items-center justify-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-white/50 mb-4">
        Visitor <span style={{ color: ACCENT }}>→</span> Enquiry <span style={{ color: ACCENT }}>→</span> CRM <span style={{ color: ACCENT }}>→</span> Follow-Up <span style={{ color: ACCENT }}>→</span> Pipeline
      </div>

      <div className="space-y-3">
        {/* 1. Landing page + form submit */}
        <div className="reveal rounded-2xl border border-white/10 bg-white/[0.05] backdrop-blur-sm p-3 shadow-[0_20px_50px_-25px_rgba(0,0,0,0.7)]">
          <div className="flex items-center gap-1.5 mb-2">
            <span className="h-2 w-2 rounded-full bg-white/20" />
            <span className="h-2 w-2 rounded-full bg-white/20" />
            <span className="h-2 w-2 rounded-full bg-white/20" />
            <div className="ml-2 flex items-center gap-1.5 rounded-md bg-white/[0.06] px-2 py-0.5 text-[9px] font-mono text-white/50">
              <Globe className="h-2.5 w-2.5" /> yourbusiness.co.za
            </div>
          </div>
          <div className="rounded-lg bg-white p-3">
            <div className="text-[11px] font-black text-zinc-900 leading-tight">Get a free consultation</div>
            <div className="mt-2 h-6 rounded-md bg-zinc-100 border border-zinc-200" />
            <div className="mt-1.5 h-6 rounded-md bg-zinc-100 border border-zinc-200" />
            <div className="mt-2 relative inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-[10px] font-bold text-zinc-900" style={{ background: ACCENT }}>
              Submit enquiry
              <MousePointerClick className="h-3 w-3 absolute -right-3 -bottom-2 text-white" />
            </div>
          </div>
        </div>

        <div className="flex justify-center"><ArrowDown className="h-4 w-4" style={{ color: ACCENT }} /></div>

        {/* 2. CRM contact card with source tag */}
        <div className="reveal rounded-2xl border border-white/10 bg-white/[0.05] backdrop-blur-sm p-3.5 shadow-[0_20px_50px_-25px_rgba(0,0,0,0.7)]" style={{ animationDelay: "0.1s" }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="h-9 w-9 rounded-full flex items-center justify-center text-[11px] font-black text-zinc-900" style={{ background: ACCENT }}>NK</div>
              <div>
                <div className="text-[12px] font-bold text-white leading-tight">Naledi Khumalo</div>
                <div className="text-[10px] text-white/50">naledi@brightco.co.za</div>
              </div>
            </div>
            <span className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/[0.06] px-2 py-0.5 text-[9px] font-bold text-white/70">
              <UserPlus className="h-2.5 w-2.5" style={{ color: ACCENT }} /> New lead
            </span>
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            <span className="rounded-md bg-white/[0.06] border border-white/10 px-2 py-0.5 text-[9px] font-semibold text-white/60">Source: Google Ads</span>
            <span className="rounded-md bg-white/[0.06] border border-white/10 px-2 py-0.5 text-[9px] font-semibold text-white/60">Campaign: Spring</span>
          </div>
        </div>

        <div className="flex justify-center"><ArrowDown className="h-4 w-4" style={{ color: ACCENT }} /></div>

        {/* 3. Automated follow-up + pipeline stage */}
        <div className="reveal grid grid-cols-2 gap-3" style={{ animationDelay: "0.2s" }}>
          <div className="rounded-2xl border border-white/10 bg-white/[0.05] backdrop-blur-sm p-3 shadow-[0_20px_50px_-25px_rgba(0,0,0,0.7)]">
            <div className="flex items-center gap-1.5 text-[10px] font-bold text-white/70">
              <MailCheck className="h-3.5 w-3.5" style={{ color: ACCENT }} /> Auto follow-up
            </div>
            <div className="mt-2 rounded-lg bg-white/[0.06] p-2 text-[9px] text-white/60 leading-snug">
              "Hi Naledi, thanks for your enquiry — here's what happens next..."
            </div>
            <div className="mt-1.5 text-[8px] font-semibold uppercase tracking-wider" style={{ color: ACCENT }}>Sent automatically</div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.05] backdrop-blur-sm p-3 shadow-[0_20px_50px_-25px_rgba(0,0,0,0.7)]">
            <div className="flex items-center gap-1.5 text-[10px] font-bold text-white/70">
              <TrendingUp className="h-3.5 w-3.5" style={{ color: ACCENT }} /> Pipeline
            </div>
            <div className="mt-2 space-y-1">
              {["New lead", "Contacted", "Qualified"].map((s, i) => (
                <div key={s} className="flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full" style={{ background: i === 1 ? ACCENT : "rgba(255,255,255,0.2)" }} />
                  <span className={`text-[9px] ${i === 1 ? "text-white font-bold" : "text-white/45"}`}>{s}</span>
                  {i === 1 && <CheckCircle2 className="h-2.5 w-2.5 ml-auto" style={{ color: ACCENT }} />}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <p data-testid="sales-hero-visual-label" className="mt-4 text-center text-[10px] uppercase tracking-[0.16em] text-white/40">
        Illustrative example · fictional data
      </p>
    </div>
  );
}

export default function SalesHero() {
  const handlePrimary = () => {
    trackEvent("cta_click", { location: "sales_hero_primary", label: "get_my_sales_system_plan" });
    scrollToForm();
  };

  return (
    <section id="home" aria-labelledby="home-heading"
      data-testid="web-hero-section"
      className="relative overflow-hidden bg-sales-navy pt-28 pb-20 sm:pt-36 sm:pb-28 text-white"
    >
      {/* grid + grain overlays */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
        <div
          className="absolute inset-0 opacity-[0.6]"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(255,255,255,0.035) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.035) 1px, transparent 1px)",
            backgroundSize: "58px 58px",
            maskImage: "radial-gradient(ellipse at 70% 20%, black 0%, transparent 70%)",
          }}
        />
      </div>

      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-[1.05fr_0.95fr] gap-14 lg:gap-10 items-center">
        {/* Left — message */}
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.06] px-3.5 py-1.5 text-[11px] tracking-[0.14em] uppercase text-white/75 font-semibold">
            <Sparkles className="h-3.5 w-3.5" style={{ color: ACCENT }} />
            Website Creation + Sales System
          </div>

          <h1 id="home-heading"
            data-testid="web-hero-h1"
            className="font-display mt-6 text-[38px] leading-[1.03] sm:text-[50px] lg:text-[58px] font-black tracking-[-0.035em]"
          >
            Business Websites That{" "}
            <span style={{ color: ACCENT }}>Capture Leads</span> and Automate Follow-Up
          </h1>

          <p data-testid="web-hero-subheadline" className="mt-7 text-lg sm:text-xl text-white/70 max-w-xl leading-relaxed">
            Rekisa builds professional business websites connected to lead capture, CRM, automated follow-up and sales pipelines.
          </p>
          <p className="mt-3 text-sm sm:text-base text-white/55 max-w-xl leading-relaxed">
            When a customer enquires, your system can record their details, respond automatically and help your team manage
            the next sales step. We create the website and connect the tools behind it, so your business can manage
            enquiries more consistently.
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-4">
            <Button
              onClick={handlePrimary}
              data-testid="web-hero-cta-request-quote"
              className="rounded-full whitespace-normal h-auto min-h-14 py-3 px-7 text-base font-bold text-[#0B1120] shadow-[0_10px_30px_-8px_rgba(190,242,100,0.5)] transition-all hover:brightness-105"
              style={{ background: ACCENT }}
            >
              Get My Sales System Plan
              <ArrowRight className="ml-1.5 h-5 w-5" />
            </Button>
            <button
              onClick={seeHowItWorks}
              data-testid="sales-hero-secondary-cta"
              className="rounded-full h-14 px-6 text-base font-semibold text-white border border-white/20 bg-white/[0.04] hover:bg-white/[0.09] transition-colors"
            >
              See How It Works
            </button>
          </div>

          {/* supporting stage line */}
          <div className="mt-8 flex flex-wrap gap-2">
            {PILL_STAGES.map((s) => (
              <span
                key={s}
                className="rounded-full border border-white/12 bg-white/[0.04] px-3 py-1 text-[11px] font-semibold text-white/60"
              >
                {s}
              </span>
            ))}
          </div>

          <p data-testid="sales-hero-value" className="mt-6 text-sm text-white/70 max-w-md leading-relaxed font-semibold">
            Your website brings in enquiries. <span style={{ color: ACCENT }}>Your sales system helps you manage what happens next.</span>
          </p>
        </div>

        {/* Right — journey visual */}
        <div className="relative">
          <JourneyVisual />
        </div>
      </div>
    </section>
  );
}
